import { useRef } from 'react';
import { Box, Button, Container, Divider, List, ListItem, ListItemText, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import { AnimatePresence, motion, useScroll, useSpring, useTransform } from 'framer-motion';
import siteLogo from '../assets/images/sitelogo1.png';
import gradPic from '../assets/images/grad-pic.JPG';
import resume from '../assets/pdf/GRAHAME-UNG-RESUME.pdf';

function MuseumPhoto({ src, caption }: { src: string; caption: string }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <motion.figure
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      style={{ margin: 0 }}
    >
      <Box sx={{ overflow: 'hidden' }}>
        <motion.img
          src={src}
          alt={caption}
          style={{ width: '100%', display: 'block', objectFit: 'cover', y }}
        />
      </Box>
      <Box component="figcaption" sx={{ mt: 1.5 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </Box>
    </motion.figure>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      <Box sx={{ py: 8 }}>
        {children}
        <Divider sx={{ mt: 6 }} />
      </Box>
    </motion.div>
  );
}

export default function HomePage() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });
  const navbarVisible = useScrollTrigger({ disableHysteresis: true, threshold: window.innerHeight * 0.5 });

  return (
    <>
      <AnimatePresence>
        {navbarVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            id="scroll-indicator"
            style={{
              scaleX,
              position: 'fixed',
              top: 72,
              left: 0,
              right: 0,
              height: 10,
              originX: 0,
              backgroundColor: '#d0d1c5',
              zIndex: 999,
            }}
          />
        )}
      </AnimatePresence>
    <Container maxWidth="md">

      {/* Logo — hero section, full viewport height */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        <img src={siteLogo} alt="Grahame Ung" style={{ maxWidth: '100%' }} />

        <AnimatePresence>
          {!navbarVisible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, y: [0, 10, 0] }}
              exit={{ opacity: 0 }}
              transition={{
                opacity: { duration: 0.8, delay: 1 },
                y: { duration: 1.5, repeat: Infinity, ease: 'easeInOut' },
              }}
              style={{ position: 'absolute', bottom: '2rem', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}
            >
              <KeyboardArrowDownIcon sx={{ fontSize: '2.5rem' }} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* About Me */}
      <Section>
        <Typography variant="h3" gutterBottom>Hello! Welcome to my website!</Typography>
        <Typography variant="h5" gutterBottom>About Me</Typography>
        <Typography variant="body1">
          <strong>My name is Grahame Ung.</strong> I am a software developer with professional
          experience in building and maintaining full-stack and front-end
          applications across a range of domains, with experience building secure, data-driven
          software and integrating with complex APIs and third-party systems. I have a strong foundation
          in software engineering methodologies, with a collaborative, product-minded approach
          to building user-centered solutions. Additionally, I am engaged in activism focused on feminism,
          Asian-American culture, and social advocacy.
        </Typography>
      </Section>

      <MuseumPhoto src={gradPic} caption="Lafayette College Commencement — May 2024" />

      {/* Education */}
      <Section>
        <Typography variant="h5" gutterBottom>Education</Typography>
        <Typography variant="body1">
          <strong>Lafayette College</strong> — Easton, PA | August 2019 – May 2024
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          B.S. in Computer Science, Minor in Women, Gender, and Sexuality Studies
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          <strong>Courses:</strong> Artificial Intelligence, Data Structures and Algorithms, Analysis
          of Algorithms, Computer Organization, Software Engineering, Principles of Programming
          Languages, Theory of Computation, Database Management Systems, Operating Systems,
          Human-Computer Interaction
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          <strong>Extracurricular:</strong> NCAA Division I Fencing Team — Competed at the
          collegiate level, demonstrating discipline, teamwork, and leadership while balancing a
          rigorous academic schedule.
        </Typography>
      </Section>

      {/* Technical Skills */}
      <Section>
        <Typography variant="h5" gutterBottom>Technical Skills</Typography>
        <Typography variant="body1">
          <strong>Languages:</strong> Java, Python, JavaScript, TypeScript, C, C++, SQL, PHP, HTML, MIPS Assembly
        </Typography>
        <Typography variant="body1" sx={{ mt: 1 }}>
          <strong>Tools &amp; Technologies:</strong> React.js, Node.js, PostgreSQL, Git
        </Typography>
      </Section>

      {/* Experience */}
      <Section>
        <Typography variant="h5" gutterBottom>Experience</Typography>

        <Typography variant="body1">
          <strong>CareSense (MedTrak, Inc.)</strong> — Philadelphia, PA (Remote) | January 2025 – April 2026
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Junior Application Developer – Build Team
        </Typography>
        <List dense disablePadding>
          {[
            'Developed and maintained full-stack applications with a strong emphasis on front-end development using React and TypeScript, supporting telehealth and hospital systems',
            'Processed and interpreted patient data in formats such as HL7 and JSON, integrating with internal systems and custom APIs',
            'Worked extensively with protected health information (PHI) in secure, HIPAA-compliant environments',
            'Built and enhanced patient-facing communication workflows, improving engagement and delivery of standardized Patient Reported Outcome (PRO) surveys',
            'Designed and optimized SQL queries to generate reports and support data-driven decision-making for clinical and operational use',
            'Contributed to the design and implementation of new systems, including Remote Therapeutic Monitoring and regulatory reporting integrations (e.g., AAOS, CMS)',
            'Collaborated with cross-functional teams including product managers, designers, and healthcare stakeholders to gather requirements and improve functionality',
            'Participated in code reviews, debugging, and refactoring to improve system performance and maintain code quality',
            'Operated in a small, fast-paced startup environment using agile methodologies, taking ownership of tasks and delivering high-impact solutions',
          ].map((item) => (
            <ListItem key={item} sx={{ pl: 2, alignItems: 'flex-start' }}>
              <ListItemText primary={`• ${item}`} />
            </ListItem>
          ))}
        </List>

        <Typography variant="body1" sx={{ mt: 3 }}>
          <strong>International Game Technology PLC</strong> — West Greenwich, RI | July 2023 – August 2023
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Project Management &amp; Software Engineering Intern
        </Typography>
        <List dense disablePadding>
          {[
            'Collaborated across the full software development lifecycle (SDLC), including requirements gathering, QA testing, and project coordination',
            'Developed Java-based applications to support internal communication and workflow efficiency',
            'Worked in an agile, team-based environment with a focus on iterative development and delivery',
          ].map((item) => (
            <ListItem key={item} sx={{ pl: 2, alignItems: 'flex-start' }}>
              <ListItemText primary={`• ${item}`} />
            </ListItem>
          ))}
        </List>
      </Section>

      {/* Hobbies & Interests */}
      <Section>
        <Typography variant="h5" gutterBottom>Hobbies &amp; Interests</Typography>
        <Typography variant="body1">
          <strong>Boy Scouts of America – Eagle Scout:</strong> Completed extensive community service
          projects and developed leadership, teamwork, and organizational skills.
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          Working on my car, making and listening to music, enjoying the great outdoors, reading,
          weight lifting, and making little coding projects for fun!
        </Typography>
      </Section>

      {/* Resume Download */}
      <Section>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h5" gutterBottom>Want to know more?</Typography>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            style={{ display: 'inline-block' }}
          >
            <Button
              variant="contained"
              size="large"
              component="a"
              href={resume}
              download="GRAHAME-UNG-RESUME.pdf"
            >
              Download a copy of my resume!
            </Button>
          </motion.div>
        </Box>
      </Section>

    </Container>
    </>
  );
}
